import { toast } from "react-toastify";
import { DDSToast } from "@web-monorepo/dds";
import { DEFAULT_ERROR_MESSAGE, getApiErrorBody, isNetworkFailure } from "#/src/utils/apiError";
import type { ReactNode } from "react";
import css from "./notification.module.css";

export type NotificationColor = "red" | "green";

const colorToVariant: Record<NotificationColor, "error" | "success"> = {
  red: "error",
  green: "success",
};

export interface NotificationArgs {
  title?: string;
  message?: ReactNode;
  color?: NotificationColor;
  autoClose?: number | false;
  /** A toast already showing with this id is not shown again. */
  toastId?: string;
}

export const showNotification = ({ title, message, color, autoClose, toastId }: NotificationArgs) => {
  const variant = color ? colorToVariant[color] : "neutral";
  const content =
    title && message ? (
      <>
        <strong>{title}</strong> {message}
      </>
    ) : (
      message || title
    );
  toast(<DDSToast variant={variant}>{content}</DDSToast>, {
    autoClose,
    toastId,
  });
};

export const getErrorHandlerWithNotification = ({
  defaultErrorMessage,
  title,
}: {
  defaultErrorMessage?: string;
  title?: string;
}) => {
  return (error: unknown) => {
    // Network failures are reported by the transport layer (axios errorHandler, or the query client's
    // QueryCache/MutationCache), unless the request opted out; those get no toast here either.
    if (isNetworkFailure(error)) return;
    showErrorNotification(title, getApiErrorBody(error)?.detail || defaultErrorMessage);
  };
};

export const showErrorNotification = (title?: string, message?: string) => {
  return showNotification({
    title: title || "Something went wrong!",
    message: message || DEFAULT_ERROR_MESSAGE,
    color: "red",
  });
};

export const showSuccessNotification = (
  message: string,
  options?: {
    actionText?: string;
    actionHref?: string;
    openInNewTab?: boolean;
  },
) => {
  const content =
    options?.actionText && options?.actionHref ? (
      <div className={css.successMessageContainer}>
        <span>{message}</span>
        <a
          href={options.actionHref}
          className={css.successActionLink}
          {...(options.openInNewTab && {
            target: "_blank",
            rel: "noopener noreferrer",
          })}
        >
          {options.actionText}
        </a>
      </div>
    ) : (
      message
    );

  return toast(<DDSToast variant="success">{content}</DDSToast>, {
    autoClose: 5000,
  });
};
